import { PerioMeasurementsBySide, Tooth, ToothPerioDraft, ToothSide, UPPER_JAW_LAST_TOOTH } from '@libs/ui-lib';
import { deepMergeObjectVersions, NullableNestedProps } from '@libs/utils';
import { PerioExamTooth, PerioExamToothSide } from '@ffd/api-client';
import { PerioTooltipToothSurface } from '../../../perio-add-treatment-screen/perio-exam-toolbar/perio-exam-toolbar.interface';
import { PerioPositionIndicatorStatus } from '../../../perio-add-treatment-screen/perio-position-indicator-tooth/perio-position-indicator-tooth.component';
import { DEFAULT_PERIO_MEASUREMENT_BY_SIDE } from '../../../perio-active-tooth-form';
import {
  LOWER_RIGHT_START,
  UPPER_LEFT_END,
  UPPER_RIGHT_END,
} from '../../../perio-add-treatment-screen/perio-position-indicator/perio-position-indicator-constants';
import { PerioJawConfig } from '../../../perio-jaw/perio-jaw.interface';
import { JAW_STARTING_TOOTH_MAP, TOOTH_COUNT } from './constants';
import {
  PerioExamState,
  PerioExamToothWithPosition,
  PerioExamTraversalStrategyStep,
  PerioJawConfigTuple,
  PerioTeethRecord,
} from './models';

/**
 * TODO: unit-test ALL those exported utility functions
 */

/**
 * Determines if the measurement order should be reversed (M→C→D instead of D→C→M).
 *
 * Rules for reversed order (M→C→D):
 * - BUCCAL (upper jaw): 9-16 (left side)
 * - LINGUAL (upper jaw): 1-8 (right side)
 * - BUCCAL (lower jaw): 17-24 (left side)
 * - LINGUAL (lower jaw): 25-32 (right side)
 *
 * Simplified logic:
 * - Upper jaw: reversed when (left+buccal) OR (right+lingual)
 * - Lower jaw: reversed when left side (both buccal and lingual)
 *
 * @param position - Tooth position number (1-32)
 * @param side - Tooth side (buccal or lingual)
 * @returns true if the measurement order should be reversed (M→C→D), false for default order (D→C→M)
 */
export function isReversedMeasurementOrder(position: number, side: ToothSide): boolean {
  const isBuccal = side === 'buccal';

  // Explicit rule mapping (reversed = M→C→D)
  // - BUCCAL (upper jaw): 9-16 (left)
  // - LINGUAL (upper jaw): 1-8 (right)
  // - BUCCAL (lower jaw): 17-24 (left)
  // - LINGUAL (lower jaw): 25-32 (right)
  const isUpperRight = position >= 1 && position <= UPPER_RIGHT_END; // 1-8
  const isUpperLeft = position >= UPPER_RIGHT_END + 1 && position <= UPPER_LEFT_END; // 9-16
  const isLowerLeft = position >= UPPER_LEFT_END + 1 && position < LOWER_RIGHT_START; // 17-24
  const isLowerRight = position >= LOWER_RIGHT_START; // 25-32

  if (isUpperLeft) return isBuccal;
  if (isUpperRight) return !isBuccal;
  if (isLowerLeft) return isBuccal;
  if (isLowerRight) return !isBuccal;

  // Safety fallback (shouldn't happen for 1-32)
  return false;
}

/**
 * Expands a jaw configuration tuple into the full traversal order *including* extracted teeth.
 */
export const getPerioExamTraversalStrategy = (jawConfig: PerioJawConfigTuple): PerioExamTraversalStrategyStep[] => {
  return jawConfig.flatMap(({ side, jaw, reverseTraversalOrder }) =>
    getSideToothPositionArray(side, JAW_STARTING_TOOTH_MAP[jaw], reverseTraversalOrder),
  );
};

/**
 * Checks if a side of a tooth has already been completed.
 *
 * @param position - Tooth number (1‑32).
 * @param currentToothSide - Side being checked (buccal/lingual).
 * @param completedSides - Accumulator of completed steps.
 */
export function isToothSideCompleted(
  position: number,
  currentToothSide: ToothSide,
  completedSides: PerioExamTraversalStrategyStep[],
): boolean {
  return completedSides.some((completedSide) => completedSide.position === position && completedSide.side === currentToothSide);
}

/**
 * Checks if a side of a tooth has already been touched.
 *
 * @param position - Tooth number (1‑32).
 * @param currentToothSide - Side being checked (buccal/lingual).
 * @param completedSides - Accumulator of touched steps.
 */
export function isToothSideTouched(position: number, currentToothSide: ToothSide, touchedSides: PerioExamTraversalStrategyStep[]): boolean {
  return touchedSides.some((touchedSide) => touchedSide.position === position && touchedSide.side === currentToothSide);
}

/**
 * Resolves the index of the *current* tooth within a traversal order.
 *
 * @param perioExamToothOrder - Ordered list of sides to be examined.
 * @param currentToothPosition - Currently highlighted tooth.
 */
export function getCurrentToothIndex(
  perioExamToothOrder: PerioExamTraversalStrategyStep[],
  currentToothPosition: PerioExamTraversalStrategyStep,
): number {
  const idx =
    perioExamToothOrder?.findIndex(
      ({ side, position }) => side === currentToothPosition.side && position === currentToothPosition.position,
    ) ?? -1;

  // `findIndex` returns -1 when not found; never propagate -1 into next/prev calculations.
  return idx >= 0 ? idx : 0;
}

/** Next index when navigating *forwards*; wraps around at the end. */
export function getNextToothIndex(
  perioExamToothOrder: PerioExamTraversalStrategyStep[],
  currentToothPosition: PerioExamTraversalStrategyStep,
): number {
  const currentDirectionIndex = getCurrentToothIndex(perioExamToothOrder, currentToothPosition);

  return currentDirectionIndex < perioExamToothOrder.length - 1 ? currentDirectionIndex + 1 : 0;
}

/** Previous index when navigating *backwards*; wraps around to the last element. */
export function getPreviousToothIndex(
  perioExamToothOrder: PerioExamTraversalStrategyStep[],
  currentToothPosition: PerioExamTraversalStrategyStep,
): number {
  const currentDirectionIndex = getCurrentToothIndex(perioExamToothOrder, currentToothPosition);

  return currentDirectionIndex > 0 ? currentDirectionIndex - 1 : perioExamToothOrder.length - 1;
}

export function getSiblingTeethForViewModel(
  teethRecord: PerioTeethRecord,
  toothOrder: PerioExamTraversalStrategyStep[],
  currentToothPosition: PerioExamTraversalStrategyStep,
): PerioExamToothWithPosition<'previous'> & PerioExamToothWithPosition<'next'> {
  const { previousTooth, previousToothPosition, previousToothIndex } = getPreviousTooth(teethRecord, toothOrder, currentToothPosition);
  const { nextTooth, nextToothPosition, nextToothIndex } = getNextTooth(teethRecord, toothOrder, currentToothPosition);

  return { previousTooth, previousToothPosition, previousToothIndex, nextTooth, nextToothPosition, nextToothIndex };
}

/**
 * Converts a tooth draft & state to a tooltip‑surface description consumed by the perio exam indicator.
 */
export function getTooltipToothSurface(
  side: ToothSide,
  tooth: ToothPerioDraft,
  currentToothPosition: PerioExamTraversalStrategyStep,
  extractedTeethPositions: number[],
  completedSides: PerioExamTraversalStrategyStep[],
  touchedSides: PerioExamTraversalStrategyStep[],
): PerioTooltipToothSurface {
  let status: PerioPositionIndicatorStatus = 'inactive';

  if (extractedTeethPositions.includes(tooth.position)) {
    status = 'extracted';
  } else if (currentToothPosition.position === tooth.position && currentToothPosition.side === side) {
    status = 'active';
  } else if (isToothSideCompleted(tooth.position, side, completedSides)) {
    status = 'done';
  } else if (isToothSideTouched(tooth.position, side, touchedSides)) {
    status = 'touched';
  }

  return {
    side,
    status,
    jaw: tooth.jaw,
    toothNumber: tooth.position,
    id: `${side}-${tooth.position}`,
  };
}

/**
 * Generates the complete list of tooltip‑surface descriptors used by the position indicator.
 */
export function generateTooltipToothSurfaceList({
  currentToothPosition,
  teethRecord,
  missingTeethPositions,
  extractedTeethPositions,
  completedSteps,
  touchedSteps,
}: PerioExamState): PerioTooltipToothSurface[] {
  const teethArray = Object.values(teethRecord);
  const upperJawTeeth = teethArray.filter((tooth) => tooth.position <= UPPER_JAW_LAST_TOOTH);
  const lowerJawTeeth = teethArray.filter((tooth) => tooth.position > UPPER_JAW_LAST_TOOTH).reverse();
  const teeth = [...upperJawTeeth, ...lowerJawTeeth];

  return teeth.flatMap((tooth) => {
    return [
      getTooltipToothSurface(
        'buccal',
        tooth,
        currentToothPosition,
        [...extractedTeethPositions, ...missingTeethPositions],
        completedSteps,
        touchedSteps,
      ),
      getTooltipToothSurface(
        'lingual',
        tooth,
        currentToothPosition,
        [...extractedTeethPositions, ...missingTeethPositions],
        completedSteps,
        touchedSteps,
      ),
    ];
  });
}

/**
 * Converts a *draft* teeth record into a list of fully‑typed `Tooth` domain objects,
 * applying default measurement structures so the UI can assume deep properties exist.
 */
export function deriveValidToothListFromTeethRecord(teethRecord: PerioTeethRecord): Tooth[] {
  return Object.values(teethRecord).map((tooth) => ({
    ...tooth,
    perio: {
      buccal: deepMergeObjectVersions(DEFAULT_PERIO_MEASUREMENT_BY_SIDE, tooth.perio.buccal),
      lingual: deepMergeObjectVersions(DEFAULT_PERIO_MEASUREMENT_BY_SIDE, tooth.perio.lingual),
    },
  })) as Tooth[];
}

export function getJawConfigForToothPosition(jawConfig: PerioJawConfigTuple, toothPosition: number, side: ToothSide): PerioJawConfig {
  return jawConfig.find((config) => config.jaw === (toothPosition <= UPPER_JAW_LAST_TOOTH ? 'upper' : 'lower') && config.side === side)!;
}

/**
 * Removes extracted teeth from the traversal order and returns the filtered order together with the
 * *new* "current" tooth.
 *
 * @param extractedTeethPositions - Numbers of the extracted teeth.
 * @param toothOrderWithExtractedIncluded - Original traversal order (may contain extracted).
 */
export function getToothPositionsFilteredByExtracted(
  extractedTeethPositions: number[],
  toothOrderWithExtractedIncluded: PerioExamTraversalStrategyStep[],
): Pick<PerioExamState, 'toothOrder' | 'currentToothPosition'> {
  const toothOrder = toothOrderWithExtractedIncluded.filter((orderItem) => !extractedTeethPositions.includes(orderItem.position));
  const currentToothPosition = toothOrder[0];

  return { toothOrder, currentToothPosition };
}

export function getToothForJawVisualisation(tooth: Tooth): Tooth {
  return {
    ...tooth,
    perio: {
      buccal: deepMergeObjectVersions(DEFAULT_PERIO_MEASUREMENT_BY_SIDE, tooth.perio.buccal),
      lingual: deepMergeObjectVersions(DEFAULT_PERIO_MEASUREMENT_BY_SIDE, tooth.perio.lingual),
    },
  };
}

export function mapToothPerioDraftToPerioExamTooth(toothDraft: ToothPerioDraft): PerioExamTooth {
  const mapNullablePerioMeasurementsBySideToPerioExamToothSide = (
    measurements: NullableNestedProps<PerioMeasurementsBySide>,
  ): PerioExamToothSide => {
    return measurements as PerioExamToothSide;
  };
  return {
    boneLoss: toothDraft.boneLoss,
    facial: mapNullablePerioMeasurementsBySideToPerioExamToothSide(toothDraft.perio.buccal!),
    lingual: mapNullablePerioMeasurementsBySideToPerioExamToothSide(toothDraft.perio.lingual!),
    mobility: toothDraft.mobility,
    position: toothDraft.position,
  } satisfies PerioExamTooth;
}

function getPreviousTooth(
  teethRecord: PerioTeethRecord,
  toothOrder: PerioExamTraversalStrategyStep[],
  currentToothPosition: PerioExamTraversalStrategyStep,
): PerioExamToothWithPosition<'previous'> {
  const previousToothIndex = getPreviousToothIndex(toothOrder, currentToothPosition);
  const previousToothPosition = toothOrder[previousToothIndex];
  return { previousTooth: teethRecord[previousToothPosition.position] as Tooth, previousToothPosition, previousToothIndex };
}

function getNextTooth(
  teethRecord: PerioTeethRecord,
  toothOrder: PerioExamTraversalStrategyStep[],
  currentToothPosition: PerioExamTraversalStrategyStep,
): PerioExamToothWithPosition<'next'> {
  const nextToothIndex = getNextToothIndex(toothOrder, currentToothPosition);
  const nextToothPosition = toothOrder[nextToothIndex];
  return { nextTooth: teethRecord[nextToothPosition.position] as Tooth, nextToothPosition, nextToothIndex };
}

/**
 * Generates an ordered list of tooth positions for the given side.
 *
 * @param side - Buccal or lingual.
 * @param startingPos - First tooth number in the jaw (defaults to 1).
 * @param reverse - When `true` the result is reversed.
 *
 * @internal Used exclusively by {@link getPerioExamTraversalStrategy}.
 */
const getSideToothPositionArray = (side: ToothSide, startingPos: number = 1, reverse = false): PerioExamTraversalStrategyStep[] => {
  const res: PerioExamTraversalStrategyStep[] = Array.from({ length: TOOTH_COUNT / 2 }).map((_, i) => ({
    side,
    position: i + startingPos,
  }));

  return reverse ? res.reverse() : res;
};
